import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=checking, false=guest, object=auth
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            // 2FA gate: backend returned a pending token instead of a user.
            if (data && data.requires_2fa) {
                return {
                    ok: true,
                    requires_2fa: true,
                    methods: data.methods || [],
                    pending_token: data.pending_token,
                };
            }
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
        }
    };

    const verify2FA = async ({ pending_token, method, code }) => {
        try {
            const { data } = await api.post("/auth/2fa/verify", { pending_token, method, code });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
        }
    };

    const request2FAEmail = async (pending_token) => {
        try {
            await api.post("/auth/2fa/email/request", { pending_token });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
        }
    };

    const register = async (email, password, name) => {
        try {
            const { data } = await api.post("/auth/register", { email, password, name });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore */
        }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, verify2FA, request2FAEmail }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
